import { useEffect, useState } from "react";
import { useTheme } from "next-themes";
import { Moon, Sun } from "lucide-react";

/**
 * Two-state theme toggle: light ⇄ dark. Light is Staff Hub's default
 * (field use, sun glare); dark is the canonical brand and one tap away.
 * The user's choice persists via next-themes (localStorage `theme`).
 *
 * The flip must not animate — the design system's `html.theme-switching`
 * rule kills every transition while the class is present. We add it,
 * flip, then remove it after two frames (one for React to commit the new
 * class, one for the browser to paint it without transitions).
 *
 * next-themes only knows the real theme after mount; until then we
 * render the light-mode icon so the first paint is stable.
 */
export function ThemeToggle({ className }: { className?: string }) {
  const { resolvedTheme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  const isDark = mounted && resolvedTheme === "dark";
  const label = isDark ? "Switch to light theme" : "Switch to dark theme";

  function toggle() {
    const root = document.documentElement;
    root.classList.add("theme-switching");
    setTheme(isDark ? "light" : "dark");
    requestAnimationFrame(() => {
      requestAnimationFrame(() => root.classList.remove("theme-switching"));
    });
  }

  return (
    <button
      type="button"
      onClick={toggle}
      title={label}
      aria-label={label}
      className={`grid h-11 w-11 place-items-center rounded-lg text-foreground-muted transition-colors duration-fast hover:bg-foreground/[0.06] hover:text-foreground ${
        className ?? ""
      }`}
    >
      {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
    </button>
  );
}

/**
 * Keeps `<meta name="theme-color">` in step with the active theme so the
 * browser chrome and — on iOS home-screen installs with the `default`
 * status-bar style — the status bar paint in cream or ink to match the
 * page. Mount once inside ThemeProvider; renders nothing.
 */
const THEME_COLOR = { light: "#F2EDE3", dark: "#131110" } as const;

export function ThemeColorSync() {
  const { resolvedTheme } = useTheme();
  useEffect(() => {
    const mode = resolvedTheme === "dark" ? "dark" : "light";
    let meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "theme-color";
      document.head.appendChild(meta);
    }
    meta.content = THEME_COLOR[mode];
  }, [resolvedTheme]);
  return null;
}
